import type { ReactNode, SVGProps } from "react";
import { cx } from "../cx";

/**
 * Shared glyph set behind `<Icon name="…" />` (OWN-173): one 24×24 viewBox, `currentColor`
 * stroke 1.75, round caps, no fill. Drawn in-house. Decorative unless `label` is given.
 * Only directional glyphs mirror in RTL (see MIRRORED).
 */
const P: Readonly<Record<string, ReactNode>> = {
  home: <path d="M4 11 12 4l8 7M6 10v9h12v-9M10 19v-5h4v5" />,
  inbox: <path d="M4 13.5 6.5 5h11L20 13.5V19H4v-5.5ZM4 13.5h4.5a3.5 3.5 0 0 0 7 0H20" />,
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 6.2A3 3 0 0 1 16 12M17.5 14a5 5 0 0 1 3 5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  plug: <path d="M9 3v5M15 3v5M6.5 8h11v3a5.5 5.5 0 0 1-11 0V8ZM12 16.5V21" />,
  sparkles: (
    <path d="m12 4 1.8 4.7L18.5 10.5l-4.7 1.8L12 17l-1.8-4.7L5.5 10.5l4.7-1.8L12 4ZM19 3v3M20.5 4.5h-3M5 16v3M6.5 17.5h-3" />
  ),
  megaphone: <path d="M4 10v4h3l8 4V6L7 10H4ZM18 9.5a3.5 3.5 0 0 1 0 5M8 14l1 5h2.5l-1-4.5" />,
  cart: (
    <>
      <path d="M3 4h2.5l2 11h10l2-8H7" />
      <circle cx="9" cy="19" r="1.2" />
      <circle cx="17" cy="19" r="1.2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
  chart: <path d="M4 20V4M4 20h16M8 16v-4M12 16V8M16 16v-6" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  filter: <path d="M4 5h16l-6.2 7.5V19l-3.6-1.8v-4.7L4 5Z" />,
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.1" />
      <circle cx="12" cy="12" r="1.1" />
      <circle cx="18.5" cy="12" r="1.1" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.6 9.6a2.5 2.5 0 1 1 3.6 2.2c-.8.4-1.2 1-1.2 1.8M12 16.8v.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.5M12 7.5v.01" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  check: <path d="m5 12.5 4.5 4.5L19 7.5" />,
  trash: <path d="M5 7h14M10 7V4.5h4V7M7 7l.8 12.5h8.4L17 7M10.5 11v5M13.5 11v5" />,
  edit: <path d="m5 19 .8-4L16.5 4.3a1.8 1.8 0 0 1 2.5 0l.7.7a1.8 1.8 0 0 1 0 2.5L9 18.2 5 19Z" />,
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11" height="11" rx="2" />
      <path d="M15.5 8.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h2.5" />
    </>
  ),
  download: <path d="M12 4v11M7.5 11 12 15.5 16.5 11M5 19.5h14" />,
  upload: <path d="M12 15.5V4.5M7.5 8.5 12 4l4.5 4.5M5 19.5h14" />,
  link: (
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1" />
  ),
  bell: <path d="M6 17V11a6 6 0 0 1 12 0v6l1.5 2h-15L6 17ZM10 21h4" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.7 3.7 5.7 3.7 9S14.6 18.3 12 21c-2.6-2.7-3.7-5.7-3.7-9S9.4 5.7 12 3Z" />
    </>
  ),
  play: <path d="M7 5v14l12-7L7 5Z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  send: <path d="M4 12 20 4l-4 16-4-7-8-1Z" />,
  attach: (
    <path d="m19 11.5-7 7a4.5 4.5 0 0 1-6.4-6.400l7.4-7.400a3 3 0 0 1 4.2 4.200L9.7 16.300a1.5 1.5 0 0 1-2.1-2.100l6.7-6.7" />
  ),
  image: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m4.5 17 4.5-4.5 3.5 3.5 2.5-2.5 4.5 4" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M6 11.500a6 6 0 0 0 12 0M12 17.500V21" />
    </>
  ),
  note: (
    <>
      <path d="M6 3.500h9l3.5 3.500v13.500H6V3.500Z" />
      <path d="M14.5 3.500V7.500h4M9 12h6M9 15.500h6" />
    </>
  ),
  tag: (
    <>
      <path d="M3.5 12.500V4.500h8l9 9-8 8-9-9Z" />
      <circle cx="8" cy="9" r="1.2" />
    </>
  ),
  folder: (
    <path d="M3.5 6.500a1.5 1.5 0 0 1 1.5-1.500h4.500l2 2.500H19a1.5 1.5 0 0 1 1.5 1.500v8.500A1.5 1.5 0 0 1 19 19.500H5A1.5 1.5 0 0 1 3.5 18V6.500Z" />
  ),
  star: <path d="m12 4 2.4 5 5.6.7-4.1 3.8 1.1 5.500L12 16.3 7 19l1.1-5.500L4 9.7 9.6 9 12 4Z" />,
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
      <path d="M8.5 10.500V7.500a3.5 3.5 0 0 1 7 0v3" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.750" />
    </>
  ),
  refresh: <path d="M19.5 12a7.5 7.5 0 1 1-2.3-5.400M19.5 4.500v4h-4" />,
  undo: <path d="M9 7 4.5 11.5 9 16M5 11.500h9a5 5 0 0 1 0 10h-1" />,
  bolt: <path d="M13 3 5.5 13.500H11L10 21l8-11h-5.500L13 3Z" />,
  template: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 9.500h16M9.5 9.500V20" />
    </>
  ),
  wallet: (
    <>
      <path d="M4 7.500A2.5 2.5 0 0 1 6.5 5H18v3" />
      <rect x="4" y="8" width="16" height="11.5" rx="2" />
      <path d="M16 13.800v.01" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="3.5" />
      <path d="m10.5 12.5 8-8M15.5 7.500l2 2M13.5 9.500l2 2" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  reply: <path d="M10 7 4.5 12 10 17M5 12h8a6 6 0 0 1 6 6v1" />,
  smile: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14a4 4 0 0 0 7 0M9 9.800v.01M15 9.800v.01" />
    </>
  ),
  flag: <path d="M5.5 21V4M5.5 5h11l-2 3.5 2 3.500h-11" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.500l3.5 2" />
    </>
  ),
  phone: (
    <path d="M6.5 3.500h3l1.5 4-2 1.500a11 11 0 0 0 6 6l1.5-2 4 1.500v3a2 2 0 0 1-2 2A15.5 15.5 0 0 1 4.5 5.500a2 2 0 0 1 2-2Z" />
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="m4 7.5 8 6 8-6" />
    </>
  ),
  // Block-palette extras (flows).
  message: <path d="M4 5.500h16v10.500h-8l-4.5 3.500V16H4V5.500Z" />,
  buttons: (
    <>
      <rect x="4" y="5" width="16" height="5" rx="1.5" />
      <rect x="4" y="14" width="16" height="5" rx="1.5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s6.5-6 6.5-11a6.5 6.5 0 0 0-13 0C5.5 15 12 21 12 21Z" />
      <circle cx="12" cy="10" r="2.2" />
    </>
  ),
  carousel: (
    <>
      <rect x="7" y="6" width="10" height="12" rx="1.5" />
      <path d="M3.5 8.500v7M20.5 8.500v7" />
    </>
  ),
  list: <path d="M9 7h11M9 12h11M9 17h11M4.5 7v.01M4.5 12v.01M4.5 17v.01" />,
  form: (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="M9 8.500h6M9 12.500h6M9 16.500h3" />
    </>
  ),
  branch: <path d="M7 4v16M7 8c0 4 10 2 10 8M17 16v4M17 4v8" />,
  arrow: <path d="M4 12h15m-5-6 6 6-6 6" />,
  flow: (
    <>
      <circle cx="6" cy="6" r="2" />
      <circle cx="18" cy="12" r="2" />
      <circle cx="6" cy="18" r="2" />
      <path d="M8 6h4a4 4 0 0 1 4 4M8 18h4a4 4 0 0 0 4-4" />
    </>
  ),
  shuffle: (
    <path d="M4 7h3.500c5 0 4 10 9 10H20M4 17h3.500c1.5 0 2.5-1 3.2-2.200M13 8.200c.8-.7 1.8-1.2 3-1.200H20M17.5 4 20.5 7l-3 3M17.5 14l3 3-3 3" />
  ),
  table: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="1.5" />
      <path d="M4 10h16M4 14.500h16M10 10v9" />
    </>
  ),
  panel: (
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M9.5 4.500v15" />
    </>
  ),
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "chevron-up": <path d="m6 15 6-6 6 6" />,
  "chevron-forward": <path d="m9 6 6 6-6 6" />,
  "chevron-back": <path d="m15 6-6 6 6 6" />,
  camera: (
    <>
      <rect x="3.5" y="7" width="17" height="12" rx="2" />
      <path d="M8.5 7 10 4.5h4L15.5 7" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  warning: <path d="M12 4 3 19.500h18L12 4ZM12 10v4.500M12 17.200v.01" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" />
    </>
  ),
  moon: <path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5Z" />,
};

export type IconName = keyof typeof P;
export const ICON_NAMES = Object.keys(P) as IconName[];

/** Glyphs that point along the reading axis: mirrored in RTL unless `mirror` says otherwise. */
const MIRRORED: ReadonlySet<string> = new Set([
  "send",
  "reply",
  "undo",
  "arrow",
  "branch",
  "chevron-forward",
  "chevron-back",
]);

export interface IconGlyphProps extends Omit<SVGProps<SVGSVGElement>, "children" | "name"> {
  readonly name: IconName | (string & {});
  readonly size?: number;
  /** Accessible name; omit for decorative icons (then `aria-hidden`). */
  readonly label?: string;
  readonly mirror?: boolean;
}

const FALLBACK = P["info"];

/** `<Icon name="filter" size={20} />` — see `ICON_NAMES`. Unknown names render the info glyph. */
export function Icon({
  name,
  size = 20,
  label,
  mirror,
  className,
  style,
  ...rest
}: IconGlyphProps) {
  const flip = mirror ?? MIRRORED.has(name);
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      data-icon={name}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      className={cx("shrink-0", flip && "rtl:-scale-x-100", className)}
      style={style}
      {...rest}
    >
      {P[name] ?? FALLBACK}
    </svg>
  );
}
